import {
  copyText,
  useModalContext,
  useWSSnackbar,
  WSFlexBox,
  WSIcon,
  WSInfiniteScroll,
  WSPage,
  WSTable,
  WSTableColumn,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import { useHistory } from "react-router-dom";
import { WSQueries, WSQuery } from "../../query/WSQuery";
import {
  useMemberProfile,
  useOrganizationUsers,
  useUserProfile
} from "../../query/users/queries";
import flatten from "lodash/flatten";
import { INewUser } from "@wingspanhq/users/dist/lib/interfaces/newUser";
import { getUserName } from "../../query/users/selectors";
import {
  ORGANIZATION_DESCRIPTION_INFO_MODAL,
  OrganizationDescriptionInfoModal
} from "../modals/OrganizationDescriptionInfoModal";
import { useRequestOrganizationUser } from "../../query/users/mutations";
import { AxiosError } from "axios";
import { IOrganizationAccount } from "@wingspanhq/users/dist/lib/interfaces/organizationAccount";

const Parent: React.FC<{ organizationAccount?: IOrganizationAccount }> = ({
  organizationAccount
}) => {
  const qUser = useMemberProfile(organizationAccount?.parentUserId as string, {
    enabled: organizationAccount?.parentUserId
  });

  if (!organizationAccount?.parentUserId) {
    return (
      <WSText.Label singleLine color="grey700">
        -
      </WSText.Label>
    );
  }

  return (
    <WSQuery
      query={qUser}
      renderLoader={() => (
        <WSText.Label singleLine color="grey700" shimmer>
          Parent
        </WSText.Label>
      )}
    >
      {user => (
        <WSText.Label singleLine color="grey700">
          {user.data.profile.company?.name ||
            user.data.user.email ||
            user.data.memberId}
        </WSText.Label>
      )}
    </WSQuery>
  );
};

export const OrganizationAccounts: React.FC = () => {
  const history = useHistory();
  const { openModal } = useModalContext();
  const { openSnackbar } = useWSSnackbar();

  const [requestUser, requestUserMeta] = useRequestOrganizationUser();

  const onSwitchAccount = (userId: string) => {
    requestUser(userId, {
      onSuccess: () => {
        window.location.reload();
      },
      onError(error: AxiosError<any>) {
        openSnackbar({
          message:
            error.response?.data?.error ||
            error.message ||
            "Something went wrong!",
          type: "warning"
        });
      }
    });
  };

  const qOrganizationUsers = useOrganizationUsers();

  const columns: Array<WSTableColumn<INewUser>> = [
    {
      config: {
        header: {
          text: "Name"
        },
        gridTemplateSizeMax: "auto"
      },
      renderFunction: row => {
        const name = getUserName(row.data);

        if (!name) {
          return (
            <WSText.Label singleLine color="grey500">
              Unnamed user
            </WSText.Label>
          );
        }

        return (
          <WSText.Label singleLine color="grey700">
            {name}
          </WSText.Label>
        );
      }
    },
    {
      config: {
        header: {
          text: "Parent(s)"
        },
        gridTemplateSizeMax: "1fr"
      },
      renderFunction: row => (
        <Parent organizationAccount={row.data.organizationAssociation} />
      )
    },
    {
      config: {
        header: {
          text: "User ID"
        },
        gridTemplateSizeMax: "1fr"
      },
      renderFunction: row => (
        <WSFlexBox.CenterY wrap="nowrap">
          <WSText.Label
            singleLine
            color="grey700"
            onClick={async () => {
              await copyText(row.data.userId);
              openSnackbar({
                message: "Copied!",
                type: "success"
              });
            }}
          >
            {`${row.data.userId}`}
          </WSText.Label>
          <WSIcon name="copy" size="S" color="grey700" />
        </WSFlexBox.CenterY>
      )
    },
    {
      config: {
        header: {
          text: "Email"
        },
        gridTemplateSizeMax: "1fr"
      },
      renderFunction: row => (
        <WSText.Label singleLine color="grey700">
          {`${row.data.email}`}
        </WSText.Label>
      )
    },
    {
      config: {
        header: {
          text: ""
        },
        gridTemplateSizeMax: "auto"
      },
      renderFunction: row => (
        <WSText.Heading4
          align="right"
          color="blue500"
          singleLine
          onClick={() => onSwitchAccount(row.data.userId)}
        >
          {`View Dashboard as ${getUserName(row.data)}`}
        </WSText.Heading4>
      )
    }
  ];

  return (
    <WSPage title="Wingspan Organization">
      <OrganizationDescriptionInfoModal />
      <WSText.Paragraph>
        Wingspan Organization Accounts allow you to programmatically create and
        manage child accounts.
        <WSIcon
          ml="XS"
          inline
          name="info"
          size="XS"
          color="blue500"
          onClick={() => openModal(ORGANIZATION_DESCRIPTION_INFO_MODAL)}
        />
      </WSText.Paragraph>

      <WSQueries queries={{ qOrganizationUsers }}>
        {({ qOrganizationUsers: { data: users } }) => {
          const usersList = flatten(users);

          const tableData = usersList.map(user => {
            return {
              data: user,
              id: user.userId
            };
          });

          return (
            <WSInfiniteScroll
              onLoad={() => {
                qOrganizationUsers.fetchMore();
              }}
              loadMore={usersList.length > 0}
              endOfList={!qOrganizationUsers.canFetchMore}
              loading={!!qOrganizationUsers.isFetchingMore}
            >
              <WSTable
                mt="XL"
                mb="M"
                tableData={tableData}
                columns={columns}
                showHeader
                bottomMessage={!usersList.length && "No accounts yet"}
              />
            </WSInfiniteScroll>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
